import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  sectionTitle: { color: "#1a237e", fontWeight: "bold", fontSize: "13px", borderBottom: "1px solid #e0e0e0", paddingBottom: "4px", marginTop: "16px", marginBottom: "4px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }
};

const ROLES = ["Principal", "Deputy Principal", "Tutor", "Senior Tutor", "Administrator", "Accountant", "Librarian", "Support Staff"];
const DEPARTMENTS = ["Academic", "Administration", "Finance", "Library", "Support"];
const CONTRACT_TYPES = ["Permanent", "Contract", "Part-time", "Volunteer"];
const GENDERS = ["Male", "Female"];

const BLANK = {
  firstName: "", lastName: "", staffNo: "", role: "", department: "", contractType: "Permanent",
  gender: "", dob: "", email: "", phone: "", nationalId: "", joinDate: "", salary: "", status: "active"
};

class StaffList extends Component {
  state = {
    tab: 0,
    staff: [],
    loading: true,
    search: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchStaff(); }

  fetchStaff = async () => {
    this.setState({ loading: true });
    try {
      const { search } = this.state;
      const params = { limit: 100 };
      if (search) params.search = search;
      const res = await api.get("/staff", { params });
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ error: "Could not load staff.", loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.firstName || !form.lastName || !form.role) {
      this.setState({ error: "First name, last name, and role are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      const payload = { ...form, salary: form.salary ? parseInt(form.salary, 10) : null };
      if (editing) await api.put(`/staff/${editing}`, payload);
      else await api.post("/staff", payload);
      this.setState({ open: false, saving: false });
      this.fetchStaff();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDeactivate = async (id, currentStatus) => {
    const action = currentStatus === "active" ? "deactivate" : "activate";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this staff member?`)) return;
    try {
      await api.patch(`/staff/${id}`, { status: currentStatus === "active" ? "inactive" : "active" });
      this.fetchStaff();
    } catch (e) { /* silent */ }
  };

  openEdit = (s) => {
    this.setState({
      open: true, editing: s.id, error: null,
      form: {
        firstName: s.firstName || "",
        lastName: s.lastName || "",
        staffNo: s.staffNo || "",
        role: s.role || "",
        department: s.department?.name || s.department || "",
        contractType: s.contractType || "Permanent",
        gender: s.gender || "",
        dob: s.dob || "",
        email: s.email || "",
        phone: s.phone || "",
        nationalId: s.nationalId || "",
        joinDate: s.joinDate || "",
        salary: s.salary != null ? String(s.salary) : "",
        status: s.status || "active"
      }
    });
  };

  render() {
    const { classes } = this.props;
    const { staff, loading, search, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "staffNo", header: "Staff No", accessorFn: s => s.staffNo || "—", meta: { variant: "mono" } },
      { id: "name", header: "Name", accessorFn: s => `${s.firstName || ""} ${s.lastName || ""}`.trim() },
      { id: "role", header: "Role", accessorFn: s => s.role || "—" },
      { id: "department", header: "Department", accessorFn: s => s.department?.name || s.department || "—" },
      { id: "contract", header: "Contract", accessorFn: s => s.contractType || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const s = info.row.original;
          return <Pill tone={s.status === "active" ? "success" : "muted"}>{s.status || "active"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const s = info.row.original;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(s)}>Edit</Button>
              <Button size="sm" color={s.status === "active" ? "danger" : "success"} simple onClick={() => this.handleDeactivate(s.id, s.status)}>
                {s.status === "active" ? "Deactivate" : "Activate"}
              </Button>
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar
            tabs={["All Staff", "Academic", "Administrative"]}
            activeTab={this.state.tab || 0}
            onTabChange={tab => this.setState({ tab })}
            actions={[]}
          />
          <div className={classes.searchBar}>
            <input
              type="text"
              placeholder="Search by name, staff no, or role..."
              value={search}
              onChange={e => this.setState({ search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && this.fetchStaff()}
              style={{ flex: 1, maxWidth: "340px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
            />
            <Button size="sm" onClick={this.fetchStaff} color="primary" style={{ marginBottom: "2px" }}>
              Search
            </Button>
          </div>

          {error && <p className={classes.errorMsg}>{error}</p>}

          <DataTable
            title="Staff Directory"
            subtitle={`${staff.length} staff member${staff.length !== 1 ? "s" : ""}`}
            actions={
              <Button size="sm" color="primary" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                + Add Staff
              </Button>
            }
            columns={columns}
            data={staff}
            isLoading={loading}
            emptyState="No staff records found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="md" fullWidth>
          <DialogHeader title={editing ? "Edit Staff Member" : "Add Staff Member"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}

            <p className={classes.sectionTitle}>Personal Details</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="First Name *" id="sfFirstName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.firstName, onChange: this.handleChange("firstName") }} />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Last Name *" id="sfLastName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.lastName, onChange: this.handleChange("lastName") }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Gender</InputLabel>
                  <Select value={form.gender} onChange={this.handleChange("gender")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker labelText="Date of Birth" id="sfDob" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.dob, onChange: this.handleChange("dob") }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput labelText="National ID" id="sfNationalId" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.nationalId, onChange: this.handleChange("nationalId") }} />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Contact</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Email" id="sfEmail" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.email, onChange: this.handleChange("email"), type: "email" }} />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Phone" id="sfPhone" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.phone, onChange: this.handleChange("phone"), type: "tel" }} />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Employment</p>
            <GridContainer>
              <GridItem xs={12} sm={4}>
                <CustomInput labelText="Staff No" id="sfStaffNo" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.staffNo, onChange: this.handleChange("staffNo"), placeholder: "auto-generated if blank" }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Role *</InputLabel>
                  <Select value={form.role} onChange={this.handleChange("role")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Department</InputLabel>
                  <Select value={form.department} onChange={this.handleChange("department")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {DEPARTMENTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Contract Type</InputLabel>
                  <Select value={form.contractType} onChange={this.handleChange("contractType")}>
                    {CONTRACT_TYPES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker labelText="Join Date" id="sfJoinDate" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.joinDate, onChange: this.handleChange("joinDate") }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput labelText="Basic Salary (UGX)" id="sfSalary" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.salary, onChange: this.handleChange("salary"), type: "number", min: "0" }} />
              </GridItem>
              {editing && (
                <GridItem xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} onChange={this.handleChange("status")}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="on_leave">On Leave</MenuItem>
                    </Select>
                  </FormControl>
                </GridItem>
              )}
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Staff")}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

StaffList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StaffList));
